
import { useCallback, useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useSupabase } from "@/hooks/useSupabase";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from '@tanstack/react-query';
import { isMockArticleId, formatArticleId } from '@/utils/articleIdValidation';

export const useArticleFavorite = (articleId: string) => {
  const { user } = useSupabase();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isCheckingFavorite, setIsCheckingFavorite] = useState(true);

  // Format article ID for database operations
  const dbArticleId = formatArticleId(articleId);

  // Check if article is favorited on mount
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user) {
        setIsCheckingFavorite(false);
        return;
      }

      try {
        const { data: favorite } = await supabase
          .from('article_favorites')
          .select('id')
          .eq('article_id', dbArticleId)
          .eq('user_id', user.id)
          .maybeSingle();

        setIsFavorited(!!favorite);
      } catch (error) {
        console.error("Error checking favorite status:", error);
      } finally {
        setIsCheckingFavorite(false);
      }
    };

    checkFavoriteStatus();
  }, [articleId, dbArticleId, user]);

  const handleFavorite = useCallback(async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to save articles",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log("Handling favorite for article:", articleId);
      console.log("Database article ID:", dbArticleId);
      
      if (isFavorited) {
        // Unfavorite
        const { error } = await supabase
          .from('article_favorites')
          .delete()
          .eq('article_id', dbArticleId)
          .eq('user_id', user.id);

        if (error) throw error;
        setIsFavorited(false);
        toast({
          title: "Article unsaved",
          description: "Article has been removed from your saved articles"
        });
      } else {
        // For mock articles, ensure they exist in the database
        if (isMockArticleId(articleId)) {
          try {
            const { data: existingArticle } = await supabase
              .from('articles')
              .select('id')
              .eq('id', dbArticleId)
              .maybeSingle();
              
            if (!existingArticle) {
              console.log("Mock article doesn't exist in DB, creating it now");
              
              const { error: insertError } = await supabase
                .from('articles')
                .insert({
                  id: dbArticleId,
                  title: `Mock Article ${articleId}`,
                  description: `This is a placeholder for mock article ${articleId}`,
                  category: 'Mock',
                  premium: false,
                  read_time: '1 min read',
                  date: new Date().toISOString()
                });
                
              if (insertError) throw insertError;
            }
          } catch (error) {
            console.error("Error checking/creating article:", error);
            throw error;
          }
        }

        // Add to favorites
        const { error } = await supabase
          .from('article_favorites')
          .insert({ 
            article_id: dbArticleId,
            user_id: user.id 
          });

        if (error) throw error;
        setIsFavorited(true);
        toast({
          title: "Article saved",
          description: "Article has been added to your saved articles"
        });

        // Invalidate saved articles query
        queryClient.invalidateQueries({
          queryKey: ['saved-articles', user.id]
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Error",
        description: "Failed to update saved status",
        variant: "destructive"
      });
    }
  }, [articleId, dbArticleId, user, toast, isFavorited, queryClient]);

  return {
    handleFavorite,
    isFavorited,
    isCheckingFavorite
  };
};
